import {useCallback} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {useWasUnmounted} from '../../../../../srcCommon/helpers/hookHelpers';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {Expression} from '../../../editors/expressions/expressionModel';
import {buildExpressionRef, ExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {useExpressionsContext} from '../../../editors/expressions/expressionsContext';
import {areInferredTypesEqual, InferredType} from '../../../editors/expressions/infer/expressionResultModel';

export function useOnSelectCreateExpression(
  onChange: HandlerOf<Expression>,
  allowedTypes?: ReadonlyArray<InferredType>,
) {
  const {hasExpressions} = useBaseEditorContext();
  const {setExpressionCreationState, setPanelOpen} = useExpressionsContext();
  const {wasUnmountedRef} = useWasUnmounted();

  const onCreateExpression = useCallback(
    (expression: Expression) => {
      if (wasUnmountedRef.current) {
        return;
      }

      if (
        allowedTypes &&
        !allowedTypes.some((type) => areInferredTypesEqual(type, expression.inferredType))
      ) {
        return;
      }

      onChange(expression);
    },
    [onChange, wasUnmountedRef, allowedTypes],
  );

  const onSelectCreateExpression = useCallback(() => {
    setPanelOpen();
    setExpressionCreationState({onCreateExpression, allowedTypes});
  }, [setPanelOpen, setExpressionCreationState, onCreateExpression, allowedTypes]);

  return hasExpressions ? onSelectCreateExpression : undefined;
}

export function useOnSelectCreateExpressionRef(
  onChange: HandlerOf<ExpressionRef>,
  allowedTypes?: ReadonlyArray<InferredType>,
) {
  const onCreateExpression = useCallback(
    (expression: Expression) => {
      onChange(buildExpressionRef(expression));
    },
    [onChange],
  );

  return useOnSelectCreateExpression(onCreateExpression, allowedTypes);
}
